import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import { Logger } from '../utils/logger';
import { deniedReason } from '../utils/portalAccess';
import { useAuth } from './AuthContext';
import { Alert, Platform } from 'react-native';
import { clearFeed, loadFeed, saveFeed, saveUnreadCount } from '../utils/notificationStore';
import {
  requestNotificationPermission,
  getFcmToken,
  onFcmTokenRefresh,
  onForegroundMessage,
  onNotificationOpenedFromBackground,
  getInitialNotification,
  registerAppNotification,
} from '../services/fcmService';
import { navigateFromNotification } from '../navigation/rootNavigation';
import { playNotificationChime } from '../utils/emergencyAlert';
import {
  startSignalR,
  stopSignalR,
  onSignalRMessage,
  isSignalRConnected,
} from '../services/signalRService';

export interface SignalRMessage {
  messageId: string;
  eventType: string;
  eventDescription: string;
  payload: any;
  correlationId?: string;
  createdAt: string;
}

const MAX_STORED_NOTIFICATIONS = 50;

interface NotificationContextType {
  subscribe: (
    channel: string,
    callback: (msg: SignalRMessage) => void
  ) => void;
  unsubscribe: (
    channel: string,
    callback: (msg: SignalRMessage) => void
  ) => void;
  unreadNotificationCount: number;
  resetUnreadCount: () => void;
  notifications: SignalRMessage[];
  /** Drops one live message from the list and its saved copy — used
   *  when the parent has seen it, so it leaves the Notifications page. */
  removeNotification: (id: string) => void;
  fcmToken: string | null;
}

const NotificationContext = createContext<NotificationContextType>({
  subscribe: () => { },
  unsubscribe: () => { },
  unreadNotificationCount: 0,
  resetUnreadCount: () => { },
  notifications: [],
  removeNotification: () => { },
  fcmToken: null,
});

export const useNotifications = () => useContext(NotificationContext);

// Local pub/sub used by feature screens (e.g. chat) to react to messages
// added to the notification list. Fed by both transports: the SignalR hub while
// the app is open, and FCM foreground pushes otherwise.
const channelCallbacks: Record<string, Set<(msg: SignalRMessage) => void>> = {};

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated: signedIn, isLoading, authError, user, students } = useAuth();
  // Signed in AND let in: a parent on the Access Denied screen (not invited, switched
  // off, nothing linked) opens no live connection and registers no phone, so school-wide
  // broadcasts and pushes cannot reach them either.
  const isAuthenticated = signedIn && !isLoading
    && deniedReason(authError, user, students, Platform.OS === 'web' ? 'web' : 'native') === null;
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState<SignalRMessage[]>([]);
  const [fcmToken, setFcmToken] = useState<string | null>(null);

  // Sorted and joined so this is stable across renders that rebuild the array
  // without changing its contents. Using `students` directly would re-register on
  // every auth refresh.
  const studentIds = students.map((s) => s.id).filter(Boolean);
  const studentKey = [...studentIds].sort().join(',');

  // Whose feed this is. The saved feed is kept per parent (utils/notificationStore), so a
  // parent signing in on a phone or browser someone else used never sees that family's alerts.
  const ownerId = user?.userId || user?.id || null;
  const ownerRef = useRef<string | null>(ownerId);
  ownerRef.current = ownerId;

  const resetUnreadCount = useCallback(async () => {
    setUnreadNotificationCount(0);
    if (ownerRef.current) saveUnreadCount(ownerRef.current, 0);
  }, []);

  const addNotification = useCallback(async (msg: SignalRMessage) => {
    // Sounds on arrival. EMERGENCY is left alone: useParentFeed already sounds the
    // siren for those, and a chime on top of it would just muddy the one alert that
    // has to be unmistakable. Chat is excluded too — an open conversation making a
    // notification noise on every message is the wrong feedback for that screen.
    if (msg.eventType !== 'Chat') {
      playNotificationChime();
    }

    // Fan out to feature screens as well as the feed. Without this, subscribe()
    // callers were registered against a channel nothing ever published to.
    const callbacks = channelCallbacks[msg.eventType];
    if (callbacks?.size) {
      callbacks.forEach((cb) => {
        try {
          cb(msg);
        } catch (e) {
          Logger.error('NotificationContext', 'A subscriber threw', e);
        }
      });
    }

    setNotifications((prev) => {
      const updated = [msg, ...prev].slice(0, MAX_STORED_NOTIFICATIONS);
      if (ownerRef.current) saveFeed(ownerRef.current, updated);
      return updated;
    });

    setUnreadNotificationCount((prev) => {
      const next = prev + 1;
      if (ownerRef.current) saveUnreadCount(ownerRef.current, next);
      return next;
    });
  }, []);

  // The feed follows the signed-in parent: their own saved copy on sign-in, nothing
  // while signed out, and on sign-out the previous parent's copy leaves the device.
  // No seeding of example notifications. An empty feed used to be filled with two
  // invented messages about Mathematics homework, which a parent had no way to tell
  // apart from a real one.
  const previousOwnerRef = useRef<string | null>(null);
  useEffect(() => {
    const previousOwner = previousOwnerRef.current;
    previousOwnerRef.current = ownerId;
    setNotifications([]);
    setUnreadNotificationCount(0);

    if (!ownerId) {
      if (previousOwner) void clearFeed(previousOwner);
      return;
    }

    let current = true;
    loadFeed<SignalRMessage>(ownerId).then(({ notifications: stored, unreadCount }) => {
      if (!current) return;
      // Anything that arrived while the saved copy was loading stays on top.
      setNotifications((live) => [...live, ...stored].slice(0, MAX_STORED_NOTIFICATIONS));
      setUnreadNotificationCount((live) => live + unreadCount);
    });
    return () => {
      current = false;
    };
  }, [ownerId]);

  // Fetches a device push token, registers it with the backend
  // (AppNotificationController) so Azure Notification Hub can target this
  // device later, and appends any foreground push to the notifications list.
  useEffect(() => {
    if (!isAuthenticated) return;
    // FCM is native-only; on web the SignalR effect below is the sole transport.
    if (Platform.OS === 'web') {
      Logger.info('FCM', 'Web platform — FCM setup skipped, notifications delivered over SignalR only');
      return;
    }

    let mounted = true;

    (async () => {
      Logger.info('FCM', '▶️ Setup starting: permission → token → backend registration');
      const granted = await requestNotificationPermission();
      if (!granted) {
        Logger.warn('FCM', '⏹️ Setup stopped: permission not granted — no token fetched, no pushes will arrive on this device');
        return;
      }

      const token = await getFcmToken();
      if (!token) {
        Logger.warn('FCM', '⏹️ Setup stopped: Firebase returned no token — device cannot receive pushes');
        return;
      }
      if (mounted) {
        setFcmToken(token);
        await registerAppNotification(token, studentIds);
        Logger.info('FCM', '✅ Setup complete: this device can now receive pushes');
      }
    })();

    const unsubscribeRefresh = onFcmTokenRefresh((token) => {
      if (mounted) setFcmToken(token);
      registerAppNotification(token, studentIds);
    });

    // Tap-through routing: a push tapped from the background or from a cold
    // start lands the parent on the Notifications screen, where the tapped
    // message is at the top of the persisted feed.
    const unsubscribeOpened = onNotificationOpenedFromBackground(() => {
      navigateFromNotification('Notifications');
    });

    getInitialNotification().then((remoteMessage) => {
      if (remoteMessage) {
        // Cold start: the navigator may not be ready yet - give it a beat.
        setTimeout(() => navigateFromNotification('Notifications'), 800);
      }
    });

    const unsubscribeForeground = onForegroundMessage((remoteMessage) => {
      Logger.debug('FCM', `Foreground push received: ${JSON.stringify(remoteMessage)}`);

      // Both transports carry the same notification, and the two messages share
      // no id to match on - so the only way to avoid a double entry in the feed
      // is to let the socket win whenever it is up. FCM then covers exactly the
      // case SignalR cannot: the app in the background, or not running.
      if (isSignalRConnected()) {
        Logger.debug('FCM', 'Ignoring foreground push - already delivered over SignalR');
        return;
      }

      const title = remoteMessage.notification?.title || 'Notification';
      const body = remoteMessage.notification?.body || '';

      if (title || body) {
        // Also add to notifications list so it shows in the notifications tab
        addNotification({
          messageId: remoteMessage.messageId || Date.now().toString(),
          eventType: 'Notification',
          eventDescription: `${title}: ${body}`,
          payload: remoteMessage.data || {},
          createdAt: new Date().toISOString(),
        });
      }
    });

    return () => {
      mounted = false;
      unsubscribeRefresh();
      unsubscribeForeground();
      unsubscribeOpened();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, studentKey]);

  // The live connection, held open for as long as the parent is signed in.
  //
  // Separate from the FCM effect on purpose: the two transports fail
  // independently, and a denied notification permission must not also cost the
  // parent their in-app updates.
  useEffect(() => {
    if (!isAuthenticated) {
      void stopSignalR();
      return;
    }

    const unsubscribe = onSignalRMessage((msg) => {
      Logger.debug('SignalR', `Notification received: ${msg.eventType}`);
      addNotification(msg);
    });

    void startSignalR();

    return () => {
      unsubscribe();
      void stopSignalR();
    };
  }, [isAuthenticated, addNotification]);

  // The id here is the same one the mapper renders with:
  // messageId when the message carried one, `${eventType}-${createdAt}` otherwise.
  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => {
      const updated = prev.filter(
        (n) => (n.messageId || `${n.eventType}-${n.createdAt}`) !== id
      );
      if (updated.length !== prev.length && ownerRef.current) {
        saveFeed(ownerRef.current, updated);
      }
      return updated;
    });
  }, []);

  const subscribe = useCallback(
    (channel: string, callback: (msg: SignalRMessage) => void) => {
      if (!channelCallbacks[channel]) {
        channelCallbacks[channel] = new Set();
      }
      channelCallbacks[channel].add(callback);
    },
    []
  );

  const unsubscribe = useCallback(
    (channel: string, callback: (msg: SignalRMessage) => void) => {
      channelCallbacks[channel]?.delete(callback);
    },
    []
  );

  return (
    <NotificationContext.Provider
      value={{
        subscribe,
        unsubscribe,
        unreadNotificationCount,
        resetUnreadCount,
        notifications,
        removeNotification,
        fcmToken,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
